// =============================================================================
// lib/identity.ts — friendly-identity helpers (Triage P0, IMG_5938/5943/5850).
//
// PROBLEM: Apple Sign-In (and fallback email signup) provisions users with an
// auto-generated handle like `d967bbgzv2`, which then leaks EVERYWHERE as the
// display name — machine garbage as identity — and the private-relay email
// (`xxxxx@privaterelay.appleid.com`) must never render on a profile surface.
//
// These helpers centralise the fix so every profile surface presents the same
// friendly fallback. Adopt in any screen that renders a user identity:
//
//   import { friendlyDisplayName, isAutoGeneratedHandle, isPrivateRelayEmail } from '../lib/identity';
//   <Text>{friendlyDisplayName(user.displayName, user.username)}</Text>
// =============================================================================

/** Apple's Hide-My-Email relay domain — never render addresses on it. */
const PRIVATE_RELAY_SUFFIX = '@privaterelay.appleid.com';

/**
 * Heuristic for a machine-generated handle (`d967bbgzv2`): 8–12 chars of
 * lowercase alphanumerics (the triage-specified regex), PLUS at least one digit
 * and one letter so real human handles like "johnsmith" are never flagged.
 */
export function isAutoGeneratedHandle(value?: string | null): boolean {
  if (!value) return false;
  const v = value.trim();
  return /^[a-z0-9]{8,12}$/.test(v) && /\d/.test(v) && /[a-z]/.test(v);
}

/**
 * True when the display name is missing OR is just the auto-generated username
 * echoed back (the `d967bbgzv2` / `d967bbgzv2` double-garbage from IMG_5938).
 */
export function hasAutoGeneratedIdentity(
  displayName?: string | null,
  username?: string | null
): boolean {
  const dn = (displayName || '').trim();
  const un = (username || '').trim();
  if (!dn) return true;
  if (!isAutoGeneratedHandle(dn)) return false;
  // An auto-gen-shaped display name that equals the username (or stands alone)
  // is machine identity, not a chosen name.
  return !un || dn.toLowerCase() === un.toLowerCase();
}

/**
 * The friendly display name for a user. Order of preference:
 *   1. a real (human-chosen) display name
 *   2. a human-looking username
 *   3. "CRYB Member" — never `d967bbgzv2` as a headline identity.
 * The @username handle should still be rendered separately (it's the address),
 * this only governs the DISPLAY line.
 */
export function friendlyDisplayName(
  displayName?: string | null,
  username?: string | null
): string {
  const dn = (displayName || '').trim();
  const un = (username || '').trim();
  if (dn && !hasAutoGeneratedIdentity(dn, un)) return dn;
  if (un && !isAutoGeneratedHandle(un)) return un;
  return 'CRYB Member';
}

/** True for Apple Hide-My-Email relay addresses. */
export function isPrivateRelayEmail(email?: string | null): boolean {
  return !!email && email.trim().toLowerCase().endsWith(PRIVATE_RELAY_SUFFIX);
}

/**
 * Mask an email for display. Private-relay addresses are NEVER shown (policy);
 * they render as "Hidden (Sign in with Apple)". Other addresses keep the first
 * two characters + domain (`jo•••@gmail.com`).
 */
export function maskEmail(email?: string | null): string {
  const e = (email || '').trim();
  if (!e) return '';
  if (isPrivateRelayEmail(e)) return 'Hidden (Sign in with Apple)';
  const [local, domain] = e.split('@');
  if (!domain) return e;
  const head = local.slice(0, 2);
  return `${head}${'•'.repeat(Math.max(1, Math.min(local.length - 2, 6)))}@${domain}`;
}
